// Shared scratch space for the signup steps.
//
// Step 2 (CreateAccount) collects the profile fields but no longer writes them
// to the API — step 3 (UploadProof) does that, and only once the proof photo
// passes verification. So the values have to survive the hop between the two
// pages in both directions: forward as the payload step 3 persists, and
// backward as the form state step 2 re-hydrates when the user hits "Back".
//
// Router state alone can't do that (it's dropped on refresh and doesn't travel
// backwards), so the draft lives in sessionStorage — tab-scoped, and stamped
// with the Firebase uid it belongs to so an abandoned draft can never pre-fill
// or be submitted for a different account in the same tab.

const STORAGE_KEY = "signupDraft";

// QA/dev builds skip step 3 (image verification) so testers can create an
// account straight from the step 2 profile form. Both `npm start` and
// `npm run build:qa` set REACT_APP_ENV=qa. The "qaproduction" build uses
// REACT_APP_ENV=production with REACT_APP_FIREBASE_ENV=qa — that combination
// also skips image verification so QA testers on the production-mode site
// are not blocked. A pure production build has neither condition, so image
// verification is always enforced there.
// Shared by Signup, CreateAccount and UploadProof so the "Step X of N"
// labels agree with the actual flow length.
export const SKIP_IMAGE_VERIFICATION =
  process.env.REACT_APP_ENV === "qa" ||
  (process.env.REACT_APP_ENV === "production" &&
    process.env.REACT_APP_FIREBASE_ENV === "qa");
export const SIGNUP_TOTAL_STEPS = SKIP_IMAGE_VERIFICATION ? 2 : 3;

// The step 2 form, labels included — the airline/home base <Select>s need the
// display names, not just the ids, to render a restored choice.
export interface SignupFormData {
  firstName: string;
  lastName: string;
  employerAirlineId: string;
  employerAirline: string;
  homebaseId: string;
  homebase: string;
  referralCode: string;
}

// What step 3 hands to UpdateAccount once verification succeeds.
export interface SignupAccountPayload {
  firstName: string;
  lastName: string;
  employerAirlineId: number;
  homebaseId: number;
  referralCode?: string;
}

export interface SignupDraft {
  uid: string;
  termsAccepted: boolean;
  formData: SignupFormData;
  accountPayload: SignupAccountPayload;
}

export const saveSignupDraft = (draft: SignupDraft): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch (e) {
    // Private-mode / quota failures are non-fatal: the forward hop still has
    // the payload in router state, only the back-and-refresh cases degrade.
  }
};

// Returns the draft only when it belongs to `uid`, so a leftover draft from an
// abandoned signup can't leak into the next one in the same tab.
export const readSignupDraft = (uid: string): SignupDraft | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const draft = JSON.parse(stored) as SignupDraft;
    return draft?.uid === uid ? draft : null;
  } catch (e) {
    return null;
  }
};

export const clearSignupDraft = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Nothing to clean up if storage is unavailable.
  }
};
